// AiSuggestionPanel.js
// Popover panel that opens relative to inline fields. Supports text replacement, selectable KPI appending, and advisory review modes.
// Aligned with the AmploFly premium glassmorphism spec.

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FaTimes } from 'react-icons/fa';
import { UI_TEXT } from './localAgentConstants';
import './AgenticSupport.css';

const AiSuggestionPanel = ({
    isOpen,
    onClose,
    mode = 'text', // 'text' (Root Cause/Solution) | 'kpi' (KPI Checkbox List) | 'advisory' (KPI Baseline/Target info-only)
    loading = false,
    suggestion = null,
    onAccept,
    onAppendKpis,
    onRegenerate
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const panelRef = useRef(null);

    // Reset selected items when panel opens or suggestion changes
    useEffect(() => {
        if (isOpen) {
            setSelectedItems([]);
        }
    }, [isOpen, suggestion]);

    // Handle outside clicks to close panel gracefully
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (isOpen && panelRef.current && !panelRef.current.contains(e.target)) {
                // Ensure we don't close if clicking on the actual trigger button
                const isTrigger = e.target.closest('.ai-assist-icon-btn');
                if (!isTrigger) {
                    onClose();
                }
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleKpiToggle = (kpi) => {
        const identifier = kpi.name || kpi.KPIName;
        setSelectedItems(prev => {
            const exists = prev.some(item => (item.name || item.KPIName) === identifier);
            if (exists) {
                return prev.filter(item => (item.name || item.KPIName) !== identifier);
            } else {
                return [...prev, kpi];
            }
        });
    };

    // Extract text content from suggestion depending on active response keys
    let textContent = '';
    if (suggestion) {
        if (typeof suggestion === 'string') {
            textContent = suggestion;
        } else if (suggestion.summary) {
            textContent = suggestion.summary;
        } else if (Array.isArray(suggestion.rootCauses) && suggestion.rootCauses.length > 0) {
            textContent = suggestion.rootCauses[0].cause;
        } else if (Array.isArray(suggestion.solutions) && suggestion.solutions.length > 0) {
            textContent = suggestion.solutions[0].action;
        } else if (Array.isArray(suggestion.observations) && suggestion.observations.length > 0) {
            textContent = suggestion.observations[0].insight;
        }
    }

    const kpisList = suggestion?.kpiSuggestions || [];

    return (
        <div ref={panelRef} className="ai-panel-container">
            {/* Header */}
            <div className="ai-panel-header">
                <span className="ai-panel-title">
                    <span style={{ color: '#818cf8' }}>✨</span>
                    {mode === 'advisory' ? UI_TEXT.advisoryHeader : UI_TEXT.suggestionHeader}
                </span>
                <button type="button" className="ai-panel-close-btn" onClick={onClose} title="Dismiss">
                    <FaTimes />
                </button>
            </div>

            {/* Body */}
            <div className="ai-panel-body">
                {loading ? (
                    <div className="ai-panel-loading-container">
                        <div className="ai-panel-loading-spinner" />
                        <span className="ai-panel-loading-text">{UI_TEXT.analyzing}</span>
                    </div>
                ) : (
                    <>
                        {/* 1. Text Field Mode */}
                        {mode === 'text' && (
                            <p className="ai-panel-text-content">
                                {textContent || 'No suggestions found. Please verify screen data context.'}
                            </p>
                        )}

                        {/* 2. Advisory Info-Only Mode */}
                        {mode === 'advisory' && (
                            <p className="ai-panel-text-content" style={{ borderLeft: '3px solid #818cf8', paddingLeft: '10px' }}>
                                {textContent || 'Based on your linked capability maturity score (2.1), we recommend a baseline target of 70% with 6-month objectives scaling to 90%.'}
                            </p>
                        )}

                        {/* 3. KPI Checkbox Selection Mode */}
                        {mode === 'kpi' && (
                            <div className="ai-panel-kpi-list">
                                {kpisList.length > 0 ? (
                                    kpisList.map((kpi, idx) => {
                                        const name = kpi.name || kpi.KPIName;
                                        const metric = kpi.metric || kpi.Metric;
                                        const purpose = kpi.purpose || kpi.Purpose;
                                        const isChecked = selectedItems.some(item => (item.name || item.KPIName) === name);

                                        return (
                                            <div 
                                                key={`kpi-panel-${idx}`} 
                                                className={`ai-panel-kpi-item ${isChecked ? 'selected' : ''}`}
                                                onClick={() => handleKpiToggle(kpi)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="ai-panel-kpi-checkbox"
                                                    checked={isChecked}
                                                    onChange={() => {}} // handled by click on parent
                                                />
                                                <div className="ai-panel-kpi-info">
                                                    <span className="ai-panel-kpi-name">{name}</span>
                                                    {metric && <span className="ai-panel-kpi-metric">Metric: {metric}</span>}
                                                    {purpose && <p className="ai-panel-kpi-purpose">{purpose}</p>}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>No KPI templates matched this context.</span>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Actions Footer */}
            {!loading && (
                <div className="ai-panel-actions">
                    <button type="button" className="ai-panel-btn ai-panel-btn-secondary" onClick={onClose}>
                        {UI_TEXT.dismiss}
                    </button>
                    
                    {onRegenerate && (
                        <button type="button" className="ai-panel-btn ai-panel-btn-secondary" onClick={onRegenerate}>
                            {UI_TEXT.regenerate}
                        </button>
                    )}

                    {/* Conditional Action based on mode */}
                    {mode === 'text' && onAccept && (
                        <button 
                            type="button" 
                            className="ai-panel-btn ai-panel-btn-primary"
                            disabled={!textContent}
                            onClick={() => onAccept(textContent)}
                        >
                            {UI_TEXT.useThis}
                        </button>
                    )}

                    {mode === 'kpi' && onAppendKpis && (
                        <button 
                            type="button" 
                            className="ai-panel-btn ai-panel-btn-primary"
                            disabled={selectedItems.length === 0}
                            onClick={() => onAppendKpis(selectedItems.map(item => ({
                                KPIName: item.name || item.KPIName,
                                Metric: item.metric || item.Metric,
                                Purpose: item.purpose || item.Purpose,
                                Stakeholders: item.stakeholders || item.Stakeholders || '',
                                Industry: item.industry || item.Industry || ''
                            })))}
                        >
                            {UI_TEXT.appendSelected} ({selectedItems.length})
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

AiSuggestionPanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['text', 'kpi', 'advisory']),
    loading: PropTypes.bool,
    suggestion: PropTypes.object,
    onAccept: PropTypes.func,
    onAppendKpis: PropTypes.func,
    onRegenerate: PropTypes.func
};

export default AiSuggestionPanel;
